import { CommunicationLanguage } from '../types/auth';
import {
  ContactPreference,
  Gender,
  IndividualMemberDto,
  MembershipType,
  ProfessionalPath,
  Study,
  StudyDegreeType,
  Title,
  WorkSector,
} from '../types/member';
import type { InternationalCommittee } from '../types/committee';
import { parseDateParts, toDateInputValue } from './dateUtils';
import { normalizeCommitteeCodes, toCommitteeCode } from './committeeUtils';
import { parseEnumFormValue, tryNormalizeEnumValue } from './enumNormalize';

function enumSelectValue<T extends Record<string, string | number>>(
  enumObj: T,
  value: unknown
): string {
  const normalized = tryNormalizeEnumValue(enumObj, value);
  return normalized != null ? String(normalized) : '';
}

export const COMMITTEES_INFO_LIST = [
  'AVICOM – Audiovisual, New Technologies and Social Media',
  'CAMOC – Museums of Cities',
  'CECA – Education and Cultural Action',
  'CIMUSET – Science and Technology',
  'CIPEG – Egyptology',
  'COMCOL – Collecting',
  'COSTUME – Costume, Fashion and Textiles',
  'DEMHIST – Historic House Museums',
  'DRMC – Disaster Resilient Museums',
  'GLASS – Glass',
  'ICAMT – Architecture and Museum Techniques',
  'ICEthics – Ethical Dilemmas',
  'ICFA – Fine Arts',
  'ICLCM – Literary and Composers’ Museums',
  'ICMAH – Archaeology and History',
  'ICME – Ethnography',
  'ICMEMOHRI – Memorial and Human Rights Museums',
  'ICOFOM – Museology',
  'ICOM ARMS & MILITARY – Arms and Military History',
  'ICOM COMMS – Communications, Marketing, and Audience Engagement',
  'ICOM CONSERVATION – Conservation',
  'ICOM DESIGN – Decorative Arts and Design',
  'ICOM DOCUMENTATION – Documentation',
  'ICOM EXHIBITIONS – Exhibition Exchange',
  'ICOM MUSIC – Instruments and Music',
  'ICOM SECURITY – Museum Security',
  'ICOM STORAGE – Collections in Storage',
  'ICOMON – Money and Banking Museums',
  'ICR – Regional Museums',
  'ICTOP – Training of Personnel',
  'INTERCOM – Museum Management',
  'NATHIST – Natural History',
  'SOMUS – Social Museology',
  'SUSTAIN – Museums and Sustainable Development',
  'UMAC – University Museums',
];

export interface MemberFormData {
  email: string;
  password: string;
  confirmPassword: string;
  communicationLanguage: CommunicationLanguage;
  title: string;
  firstNameEn: string;
  firstNameGr: string;
  lastNameEn: string;
  lastNameGr: string;
  gender: string;
  dateOfBirth: string;
  /** Incomplete DOB selections preserved across registration steps. */
  dateOfBirthDay: string;
  dateOfBirthMonth: string;
  dateOfBirthYear: string;
  residentialAddressEn: string;
  residentialAddressGr: string;
  residentialCityEn: string;
  residentialCityGr: string;
  residentialPostalCode: string;
  residentialCountryEn: string;
  residentialCountryGr: string;
  phone: string;
  mobile: string;
  website: string;
  contactPreference: string;
  professionalPath: string;
  professionalActivities: { working: boolean; studying: boolean; retired: boolean };
  organisationNameEn: string;
  organisationNameGr: string;
  professionalTitleEn: string;
  professionalTitleGr: string;
  workSector: string;
  professionalInstitutionNameEn: string;
  professionalInstitutionNameGr: string;
  professionalAddressEn: string;
  professionalAddressGr: string;
  professionalCityEn: string;
  professionalCityGr: string;
  professionalPostalCode: string;
  professionalEmail: string;
  professionalPhone: string;
  studies: Study[];
  isGraduateStudent: boolean;
  isRetired: boolean;
  isVolunteer: boolean;
  isHonoraryMember: boolean;
  membershipType: MembershipType;
  mainCommittee: string;
  additionalCommittees: string[];
  transferFromIcom: string;
  previousCardNumber: string;
  cv: File | null;
  professionalActivityDocuments: File | null;
  studentID: File | null;
  declarationDate: string;
  acceptedEthics: boolean;
  acceptedGdpr: boolean;
}

export function createEmptyMemberForm(
  communicationLanguage: CommunicationLanguage = CommunicationLanguage.English
): MemberFormData {
  return {
    email: '',
    password: '',
    confirmPassword: '',
    communicationLanguage,
    title: '',
    firstNameEn: '',
    firstNameGr: '',
    lastNameEn: '',
    lastNameGr: '',
    gender: '',
    dateOfBirth: '',
    dateOfBirthDay: '',
    dateOfBirthMonth: '',
    dateOfBirthYear: '',
    residentialAddressEn: '',
    residentialAddressGr: '',
    residentialCityEn: '',
    residentialCityGr: '',
    residentialPostalCode: '',
    residentialCountryEn: '',
    residentialCountryGr: '',
    phone: '',
    mobile: '',
    website: '',
    contactPreference: '',
    professionalPath: '',
    professionalActivities: { working: false, studying: false, retired: false },
    organisationNameEn: '',
    organisationNameGr: '',
    professionalTitleEn: '',
    professionalTitleGr: '',
    workSector: '',
    professionalInstitutionNameEn: '',
    professionalInstitutionNameGr: '',
    professionalAddressEn: '',
    professionalAddressGr: '',
    professionalCityEn: '',
    professionalCityGr: '',
    professionalPostalCode: '',
    professionalEmail: '',
    professionalPhone: '',
    studies: [],
    isGraduateStudent: false,
    isRetired: false,
    isVolunteer: false,
    isHonoraryMember: false,
    membershipType: MembershipType.Regular,
    mainCommittee: '',
    additionalCommittees: [],
    transferFromIcom: '',
    previousCardNumber: '',
    cv: null,
    professionalActivityDocuments: null,
    studentID: null,
    declarationDate: '',
    acceptedEthics: false,
    acceptedGdpr: false,
  };
}

export function getEffectiveProfessionalPath(
  activities: MemberFormData['professionalActivities']
): string {
  const { working, studying, retired } = activities;
  if (working) return ProfessionalPath.ActiveMuseumProfessional.toString();
  if (studying) return ProfessionalPath.Student.toString();
  if (retired) return ProfessionalPath.InterestedNonProfessional.toString();
  return '';
}

export function memberDtoToFormData(
  memberData: IndividualMemberDto,
  committees: InternationalCommittee[],
  communicationLanguage: CommunicationLanguage
): MemberFormData {
  const path = memberData.professionalPath;
  const hasWork =
    path === ProfessionalPath.ActiveMuseumProfessional ||
    !!(memberData.professionalInstitutionName?.en || memberData.professionalInstitutionName?.gr);
  const hasStudies =
    (memberData.studies?.length ?? 0) > 0 || memberData.isGraduateStudent;

  return {
    ...createEmptyMemberForm(communicationLanguage),
    email: memberData.email ?? '',
    title: enumSelectValue(Title, memberData.title),
    firstNameEn: memberData.name?.firstNameEn ?? '',
    firstNameGr: memberData.name?.firstNameGr ?? '',
    lastNameEn: memberData.name?.lastNameEn ?? '',
    lastNameGr: memberData.name?.lastNameGr ?? '',
    gender: enumSelectValue(Gender, memberData.gender),
    dateOfBirth: toDateInputValue(memberData.dateOfBirth),
    ...(() => {
      const dobParts = parseDateParts(toDateInputValue(memberData.dateOfBirth));
      return {
        dateOfBirthDay: dobParts.day,
        dateOfBirthMonth: dobParts.month,
        dateOfBirthYear: dobParts.year,
      };
    })(),
    residentialAddressEn: memberData.residential?.addressEn ?? '',
    residentialAddressGr: memberData.residential?.addressGr ?? '',
    residentialCityEn: memberData.residential?.cityEn ?? '',
    residentialCityGr: memberData.residential?.cityGr ?? '',
    residentialPostalCode:
      memberData.residential?.postalCodeEn ?? memberData.residential?.postalCodeGr ?? '',
    residentialCountryEn: memberData.residential?.countryEn ?? '',
    residentialCountryGr: memberData.residential?.countryGr ?? '',
    phone: memberData.phone ?? '',
    mobile: memberData.mobile ?? '',
    website: memberData.website ?? '',
    contactPreference: enumSelectValue(ContactPreference, memberData.contactPreference),
    professionalPath: enumSelectValue(ProfessionalPath, memberData.professionalPath),
    professionalActivities: {
      working: hasWork,
      studying: hasStudies,
      retired: memberData.isRetired ?? false,
    },
    organisationNameEn: memberData.organisationName?.en ?? '',
    organisationNameGr: memberData.organisationName?.gr ?? '',
    professionalTitleEn: memberData.professionalTitle?.en ?? '',
    professionalTitleGr: memberData.professionalTitle?.gr ?? '',
    workSector: enumSelectValue(WorkSector, memberData.workSector),
    professionalInstitutionNameEn: memberData.professionalInstitutionName?.en ?? '',
    professionalInstitutionNameGr: memberData.professionalInstitutionName?.gr ?? '',
    professionalAddressEn:
      memberData.professionalAddressEn ?? memberData.professionalAddress ?? '',
    professionalAddressGr: memberData.professionalAddressGr ?? '',
    professionalCityEn: memberData.professionalCityEn ?? memberData.professionalCity ?? '',
    professionalCityGr: memberData.professionalCityGr ?? '',
    professionalPostalCode: memberData.professionalPostalCode ?? '',
    professionalEmail: memberData.professionalEmail ?? '',
    professionalPhone: memberData.professionalPhone ?? '',
    studies: (memberData.studies ?? []).map((s: Study, i: number) => ({
      id: String((s as Study & { id?: string })?.id ?? i),
      titleEn: s?.titleEn ?? '',
      titleGr: s?.titleGr ?? '',
      degreeType: s?.degreeType ?? StudyDegreeType.Other,
      institutionEn: s?.institutionEn ?? (s as Study & { institution?: string }).institution ?? '',
      institutionGr: s?.institutionGr ?? '',
      isCurrent: s?.isCurrent ?? false,
      startYear: s?.startYear,
      duration: s?.duration,
      completionYear: s?.completionYear,
    })),
    isGraduateStudent: memberData.isGraduateStudent ?? false,
    isRetired: memberData.isRetired ?? false,
    isVolunteer: memberData.isVolunteer ?? false,
    isHonoraryMember: memberData.isHonoraryMember ?? false,
    membershipType: (tryNormalizeEnumValue(MembershipType, memberData.membershipType) ??
      MembershipType.Regular) as MembershipType,
    mainCommittee: toCommitteeCode(committees, memberData.mainCommittee),
    additionalCommittees: normalizeCommitteeCodes(committees, memberData.additionalCommittees),
    transferFromIcom: memberData.transferFromIcom ?? '',
    previousCardNumber: memberData.previousCardNumber ?? '',
    acceptedEthics: true,
    acceptedGdpr: true,
  };
}

export type MemberProfileSectionId =
  | 'personal'
  | 'activities'
  | 'work'
  | 'studies'
  | 'committees';

/**
 * Builds a section-scoped update DTO so unrelated fields are not serialized and
 * cannot produce phantom change requests (Name/Residential/etc.).
 */
export function buildMemberUpdateDto(
  formData: MemberFormData,
  member: IndividualMemberDto,
  committees: InternationalCommittee[],
  sectionId?: MemberProfileSectionId
): Partial<IndividualMemberDto> {
  const effectivePath = getEffectiveProfessionalPath(formData.professionalActivities);
  const section = sectionId ?? 'personal';

  if (section === 'personal') {
    return {
      id: member.id,
      title: (parseEnumFormValue(Title, formData.title) as Title | undefined) ?? member.title,
      name: {
        firstNameEn: formData.firstNameEn || undefined,
        firstNameGr: formData.firstNameGr || undefined,
        lastNameEn: formData.lastNameEn || undefined,
        lastNameGr: formData.lastNameGr || undefined,
      },
      residential: {
        addressEn: formData.residentialAddressEn || undefined,
        addressGr: formData.residentialAddressGr || undefined,
        cityEn: formData.residentialCityEn || undefined,
        cityGr: formData.residentialCityGr || undefined,
        postalCodeEn: formData.residentialPostalCode || undefined,
        postalCodeGr: formData.residentialPostalCode || undefined,
        countryEn: formData.residentialCountryEn || undefined,
        countryGr: formData.residentialCountryGr || undefined,
      },
      gender: parseEnumFormValue(Gender, formData.gender) as Gender | undefined,
      dateOfBirth: formData.dateOfBirth || undefined,
      phone: formData.phone.trim(),
      mobile: formData.mobile.trim(),
      contactPreference: parseEnumFormValue(ContactPreference, formData.contactPreference) as
        | ContactPreference
        | undefined,
    };
  }

  if (section === 'activities') {
    return {
      id: member.id,
      professionalPath: effectivePath
        ? (parseInt(effectivePath, 10) as ProfessionalPath)
        : undefined,
      isGraduateStudent: formData.isGraduateStudent,
      isRetired: formData.isRetired,
      transferFromIcom: formData.transferFromIcom || undefined,
      previousCardNumber: formData.previousCardNumber || undefined,
    };
  }

  if (section === 'work') {
    return {
      id: member.id,
      professionalTitle:
        formData.professionalTitleEn || formData.professionalTitleGr
          ? {
              en: formData.professionalTitleEn || undefined,
              gr: formData.professionalTitleGr || undefined,
            }
          : undefined,
      professionalInstitutionName:
        formData.professionalInstitutionNameEn || formData.professionalInstitutionNameGr
          ? {
              en: formData.professionalInstitutionNameEn || undefined,
              gr: formData.professionalInstitutionNameGr || undefined,
            }
          : undefined,
      workSector: parseEnumFormValue(WorkSector, formData.workSector) as WorkSector | undefined,
      professionalAddressEn: formData.professionalAddressEn || undefined,
      professionalAddressGr: formData.professionalAddressGr || undefined,
      professionalCityEn: formData.professionalCityEn || undefined,
      professionalCityGr: formData.professionalCityGr || undefined,
      professionalPostalCode: formData.professionalPostalCode || undefined,
      professionalEmail: formData.professionalEmail || undefined,
      professionalPhone: formData.professionalPhone || undefined,
      website: formData.website || undefined,
      organisationName:
        formData.organisationNameEn || formData.organisationNameGr
          ? {
              en: formData.organisationNameEn || undefined,
              gr: formData.organisationNameGr || undefined,
            }
          : undefined,
      isVolunteer: false,
    };
  }

  if (section === 'studies') {
    return {
      id: member.id,
      studies: formData.studies ?? [],
    };
  }

  // committees
  return {
    id: member.id,
    mainCommittee: toCommitteeCode(committees, formData.mainCommittee) || undefined,
    additionalCommittees: normalizeCommitteeCodes(committees, formData.additionalCommittees),
  };
}
